import {
    Backdrop,
    Badge,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Container,
    IconButton,
    Tooltip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
    withStyles,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { CloudDownload, FilterList, ListAlt, MoreVert, Search } from "@material-ui/icons"
import { format, parseISO } from "date-fns"
import { useSnackbar } from "../../utils/notificacoes"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { baixarCsv } from "../../functions/exportarCsv"
import {
    BuscarFormaDePagamentos,
    buscarPedidosListagem,
    buscarResumoPedidosListagem,
    buscarestabelecimentos,
    exportarPedidosListagem,
} from "../../services/integracao"
import useAppStore from "../../stores/AppStore"
import Cabecalho from "../Cabecalho"
import Filtros, { VERDE, VERDE_ESCURO } from "./Filtros"
import MenuAcoesPedido from "./MenuAcoesPedido"
import ResumoPedidos from "./ResumoPedidos"
import { corStatusPill, formatarMoeda, plataformaPedido, tipoPill } from "./constants"

const useStyles = makeStyles(theme => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 999999,
        color: "#fff",
    },
    appContainer: {
        paddingTop: "6em",
        backgroundColor: "#f5f7fa",
        paddingBottom: "2em",
        minHeight: "100vh",
    },
    cabecalhoPagina: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 20,
        flexWrap: "wrap",
    },
    titulo: {
        fontWeight: 700,
        color: "#1a1f36",
        fontSize: 28,
        lineHeight: 1.2,
    },
    subtitulo: {
        color: "#6b7280",
        marginTop: 4,
    },
    // Cor fixa (verde), independente da cor do layout do estabelecimento.
    btnPrimario: {
        backgroundColor: VERDE,
        color: "white",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 8,
        padding: "8px 18px",
        boxShadow: "0 2px 8px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: VERDE_ESCURO },
        "&.Mui-disabled": { backgroundColor: "#a5d6a7", color: "white", boxShadow: "none" },
    },
    btnExportar: {
        backgroundColor: VERDE,
        color: "white",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 8,
        padding: "8px 16px",
        boxShadow: "0 2px 8px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: VERDE_ESCURO },
        "&.Mui-disabled": { backgroundColor: "#a5d6a7", color: "white", boxShadow: "none" },
    },
    acoesTopo: {
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
    },
    badgeFiltros: {
        backgroundColor: VERDE,
        color: "white",
        fontWeight: 700,
    },
    btnSecundario: {
        backgroundColor: "white",
        color: "#374151",
        border: "1px solid #d1d5db",
        textTransform: "none",
        fontWeight: 500,
        borderRadius: 8,
        padding: "8px 16px",
        "&:hover": { backgroundColor: "#f9fafb" },
    },
    card: {
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        padding: 20,
    },
    pill: {
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    tabelaContainer: {
        boxShadow: "none",
        border: "none",
        marginTop: 8,
    },
    paginacao: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        marginTop: 20,
        paddingTop: 16,
        borderTop: "1px solid #f0f0f0",
    },
}))

const StyledTableCell = withStyles({
    head: {
        backgroundColor: "transparent",
        color: "#6b7280",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        borderBottom: "1px solid #e5e7eb",
        paddingTop: 12,
        paddingBottom: 12,
    },
    body: {
        fontSize: 14,
        color: "#1a1f36",
        borderBottom: "1px solid #f3f4f6",
        paddingTop: 14,
        paddingBottom: 14,
    },
})(TableCell)

const ONE_YEAR_MS = 366 * 24 * 60 * 60 * 1000
const API_DATE_FORMAT = "yyyy-MM-dd HH:mm:ss"

const inicioDoDia = (d = new Date()) => {
    const x = new Date(d)
    x.setHours(0, 0, 0, 0)
    return x
}
const fimDoDia = (d = new Date()) => {
    const x = new Date(d)
    x.setHours(23, 59, 59, 999)
    return x
}

const primeiroDiaDoMes = (d = new Date()) => inicioDoDia(new Date(d.getFullYear(), d.getMonth(), 1))

const filtrosPadrao = () => ({
    dataInicio: primeiroDiaDoMes(),
    dataFim: fimDoDia(),
    nomeCliente: "",
    telefone: "",
    codigoPedido: "",
    senha: "",
    cupomDesconto: "",
    idsFormasPagamento: [],
    tiposPedido: [],
    situacoes: [],
    plataformas: [],
    tiposFormaPagamento: [],
    estabelecimentoFiltro: "",
    temNotaFiscal: null,
    exibirPedidosNaoConfirmados: false,
    exibirExpirados: false,
})

const parseDataParam = s => {
    if (!s) return null
    try {
        const d = parseISO(s)
        return Number.isNaN(d.getTime()) ? null : d
    } catch {
        return null
    }
}

const lerFiltrosDaURL = (search, padrao) => {
    const sp = new URLSearchParams(search)
    const filtros = { ...padrao }

    const dataInicio = parseDataParam(sp.get("dataInicio"))
    const dataFim = parseDataParam(sp.get("dataFim"))
    if (dataInicio) filtros.dataInicio = dataInicio
    if (dataFim) filtros.dataFim = dataFim

    ;["nomeCliente", "telefone", "codigoPedido", "senha", "cupomDesconto", "estabelecimentoFiltro"].forEach(
        k => {
            const v = sp.get(k)
            if (v !== null) filtros[k] = v
        }
    )

    ;["idsFormasPagamento", "tiposPedido"].forEach(k => {
        const v = sp.get(k)
        if (v) filtros[k] = v.split(",").map(x => Number(x)).filter(x => !Number.isNaN(x))
    })
    ;["situacoes", "plataformas", "tiposFormaPagamento"].forEach(k => {
        const v = sp.get(k)
        if (v) filtros[k] = v.split(",").filter(Boolean)
    })

    const nfce = sp.get("temNotaFiscal")
    if (nfce === "true") filtros.temNotaFiscal = true
    else if (nfce === "false") filtros.temNotaFiscal = false

    ;["exibirPedidosNaoConfirmados", "exibirExpirados"].forEach(k => {
        if (sp.get(k) === "true") filtros[k] = true
    })

    const page = parseInt(sp.get("page") || "1", 10)
    const size = parseInt(sp.get("size") || "100", 10)
    return { filtros, page: page > 0 ? page : 1, size: size > 0 ? size : 100 }
}

const escreverFiltrosNaURL = (filtros, page, size) => {
    const sp = new URLSearchParams()
    if (filtros.dataInicio) sp.set("dataInicio", filtros.dataInicio.toISOString())
    if (filtros.dataFim) sp.set("dataFim", filtros.dataFim.toISOString())
    ;["nomeCliente", "telefone", "codigoPedido", "senha", "cupomDesconto", "estabelecimentoFiltro"].forEach(
        k => {
            if (filtros[k]) sp.set(k, filtros[k])
        }
    )
    ;["idsFormasPagamento", "tiposPedido", "situacoes", "plataformas", "tiposFormaPagamento"].forEach(
        k => {
            if (filtros[k]?.length) sp.set(k, filtros[k].join(","))
        }
    )
    if (filtros.temNotaFiscal === true) sp.set("temNotaFiscal", "true")
    else if (filtros.temNotaFiscal === false) sp.set("temNotaFiscal", "false")
    ;["exibirPedidosNaoConfirmados", "exibirExpirados"].forEach(k => {
        if (filtros[k]) sp.set(k, "true")
    })
    sp.set("page", String(page))
    sp.set("size", String(size))
    return sp.toString()
}

const filtrosParaApi = (filtros, page, size) => ({
    dataInicio: filtros.dataInicio ? format(filtros.dataInicio, API_DATE_FORMAT) : undefined,
    dataFim: filtros.dataFim ? format(filtros.dataFim, API_DATE_FORMAT) : undefined,
    nomeCliente: filtros.nomeCliente || undefined,
    telefone: filtros.telefone || undefined,
    codigoPedido: filtros.codigoPedido || undefined,
    senha: filtros.senha?.trim() || undefined,
    cupomDesconto: filtros.cupomDesconto || undefined,
    idsFormasPagamento: filtros.idsFormasPagamento,
    tiposPedido: filtros.tiposPedido,
    situacoes: filtros.situacoes,
    plataformas: filtros.plataformas,
    tiposFormaPagamento: filtros.tiposFormaPagamento,
    estabelecimentoFiltro: filtros.estabelecimentoFiltro || undefined,
    temNotaFiscal: filtros.temNotaFiscal,
    exibirPedidosNaoConfirmados: !!filtros.exibirPedidosNaoConfirmados,
    exibirExpirados: !!filtros.exibirExpirados,
    page,
    size,
})

// createdAt vem como "yyyy-MM-dd HH:mm" (já no fuso do estabelecimento) → "dd/MM/yyyy HH:mm".
const formatarDataHora = p => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(p.createdAt || "")
    if (m) return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`
    return p.dateFormatted || ""
}

// Cancelamento liberado para pedidos não cancelados com até 15 dias (mesma regra da tela web).
const QUINZE_DIAS_MS = 15 * 24 * 60 * 60 * 1000
const podeCancelar = p => {
    if (p.status == null || p.status < 0) return false
    const criado = p.createdAt ? new Date(p.createdAt.replace(" ", "T")) : null
    if (!criado || Number.isNaN(criado.getTime())) return true
    return Date.now() - criado.getTime() < QUINZE_DIAS_MS
}

// Quantidade de filtros avançados em uso (badge do botão).
const contarFiltrosAtivos = f =>
    ["nomeCliente", "telefone", "senha", "cupomDesconto", "estabelecimentoFiltro"].filter(k => f[k])
        .length +
    ["idsFormasPagamento", "tiposPedido", "situacoes", "plataformas", "tiposFormaPagamento"].filter(
        k => f[k]?.length
    ).length +
    (f.temNotaFiscal === null || f.temNotaFiscal === undefined ? 0 : 1) +
    (f.exibirPedidosNaoConfirmados ? 1 : 0) +
    (f.exibirExpirados ? 1 : 0)

export default function HistoricoPedidos() {
    const classes = useStyles()
    const theme = useTheme()
    const mobile = useMediaQuery(theme.breakpoints.down("md"))
    const history = useHistory()
    const location = useLocation()
    const { enqueueSnackbar } = useSnackbar()
    const autorizacao = localStorage.getItem("chaveSistema")

    const alertStart = useCallback(
        (msg, tipo) => enqueueSnackbar(msg, { variant: tipo }),
        [enqueueSnackbar]
    )

    const inicialRef = useRef(lerFiltrosDaURL(location.search, filtrosPadrao()))
    const [filtros, setFiltros] = useState(inicialRef.current.filtros)
    const [page, setPage] = useState(inicialRef.current.page)
    const [size] = useState(inicialRef.current.size)
    const [filtrosAvancadosAbertos, setFiltrosAvancadosAbertos] = useState(false)

    const estabelecimentoAtual = useAppStore(s => s.configuracoes?.user?.parametros?.estabelecimento)
    const idEstabelecimentoPrincipal = estabelecimentoAtual?.id

    const [estabelecimentosRede, setEstabelecimentosRede] = useState([])
    const [formasPagamento, setFormasPagamento] = useState([])

    const [pedidos, setPedidos] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [erro, setErro] = useState("")

    const [menuAcoes, setMenuAcoes] = useState(null)
    const [resumo, setResumo] = useState(null)
    const [carregandoResumo, setCarregandoResumo] = useState(false)
    const [erroResumo, setErroResumo] = useState("")
    const ultimaBuscaResumo = useRef(0)
    const [exportando, setExportando] = useState(null)

    const parametros = useAppStore(s => s.configuracoes?.user?.parametros)
    const fiscalAtivo = !!(parametros?.ativarPlugNotas || parametros?.cnpjEstabelecimentoPlugNotas)

    // Conecta o QZ-Tray ao abrir a tela para a reimpressão sair direto na impressora.
    const buscarImpressoras = useAppStore(s => s.buscarImpressoras)
    useEffect(() => {
        buscarImpressoras()
    }, [buscarImpressoras])

    const validarPeriodo = useCallback(filtros => {
        if (!filtros.dataInicio || !filtros.dataFim) {
            return "Informe um período válido."
        }
        if (filtros.dataFim.getTime() < filtros.dataInicio.getTime()) {
            return "A data fim deve ser posterior à data início."
        }
        if (filtros.dataFim.getTime() - filtros.dataInicio.getTime() > ONE_YEAR_MS) {
            return "O período máximo permitido é de 1 ano."
        }
        return null
    }, [])

    // Resumo de TODOS os pedidos do filtro (independe da página). Ignora respostas de buscas
    // antigas quando o usuário pesquisa de novo antes de terminar.
    const buscarResumo = useCallback(
        async filtrosAtuais => {
            const id = ++ultimaBuscaResumo.current
            setCarregandoResumo(true)
            setErroResumo("")
            const resp = await buscarResumoPedidosListagem(
                idEstabelecimentoPrincipal,
                filtrosParaApi(filtrosAtuais),
                autorizacao
            )
            if (id !== ultimaBuscaResumo.current) return
            setCarregandoResumo(false)
            if (resp.retornoErro) {
                setErroResumo(resp.mensagem || "Não foi possível carregar o resumo.")
                setResumo(null)
                return
            }
            setResumo(resp)
        },
        [idEstabelecimentoPrincipal, autorizacao]
    )

    const buscarPedidos = useCallback(
        async (filtrosAtuais, paginaAtual, tamanhoAtual, comResumo = true) => {
            if (!idEstabelecimentoPrincipal) return
            const erroPeriodo = validarPeriodo(filtrosAtuais)
            if (erroPeriodo) {
                setErro(erroPeriodo)
                alertStart(erroPeriodo, "warning")
                return
            }
            if (comResumo) buscarResumo(filtrosAtuais)
            setLoading(true)
            setErro("")
            const resp = await buscarPedidosListagem(
                idEstabelecimentoPrincipal,
                filtrosParaApi(filtrosAtuais, paginaAtual, tamanhoAtual),
                autorizacao
            )
            setLoading(false)
            if (resp.retornoErro) {
                setErro(resp.mensagem || "Erro ao buscar pedidos.")
                setPedidos([])
                setTotal(0)
                return
            }
            setPedidos(resp.pedidos)
            setTotal(resp.total)
        },
        [idEstabelecimentoPrincipal, autorizacao, alertStart, validarPeriodo, buscarResumo]
    )

    useEffect(() => {
        let cancelado = false
        const carregar = async () => {
            const resp = await buscarestabelecimentos(autorizacao)
            if (cancelado) return
            if (resp?.retornoErro) return
            setEstabelecimentosRede(Array.isArray(resp) ? resp : [])
        }
        carregar()
        return () => {
            cancelado = true
        }
    }, [autorizacao])

    useEffect(() => {
        if (!idEstabelecimentoPrincipal) return
        let cancelado = false
        const carregar = async () => {
            const resp = await BuscarFormaDePagamentos(idEstabelecimentoPrincipal)
            if (cancelado) return
            if (resp?.retornoErro) {
                setFormasPagamento([])
                return
            }
            setFormasPagamento(Array.isArray(resp) ? resp : resp?.formasPagamento || [])
        }
        carregar()
        return () => {
            cancelado = true
        }
    }, [idEstabelecimentoPrincipal])

    useEffect(() => {
        if (!idEstabelecimentoPrincipal) return
        buscarPedidos(filtros, page, size)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idEstabelecimentoPrincipal])

    const handleBuscar = () => {
        const novaPage = 1
        setPage(novaPage)
        const qs = escreverFiltrosNaURL(filtros, novaPage, size)
        history.replace({ pathname: location.pathname, search: qs })
        buscarPedidos(filtros, novaPage, size)
    }

    const handleLimpar = () => {
        const padrao = filtrosPadrao()
        setFiltros(padrao)
        setPage(1)
        const qs = escreverFiltrosNaURL(padrao, 1, size)
        history.replace({ pathname: location.pathname, search: qs })
        buscarPedidos(padrao, 1, size)
    }

    const handleTrocarPagina = novaPage => {
        setPage(novaPage)
        const qs = escreverFiltrosNaURL(filtros, novaPage, size)
        history.replace({ pathname: location.pathname, search: qs })
        buscarPedidos(filtros, novaPage, size, false)
    }

    const setCampo = (campo, valor) => setFiltros(prev => ({ ...prev, [campo]: valor }))

    const exportar = async detalhado => {
        const erroPeriodo = validarPeriodo(filtros)
        if (erroPeriodo) {
            alertStart(erroPeriodo, "warning")
            return
        }
        setExportando(detalhado ? "detalhado" : "simples")
        const resp = await exportarPedidosListagem(
            idEstabelecimentoPrincipal,
            filtrosParaApi(filtros),
            autorizacao,
            detalhado
        )
        setExportando(null)
        if (resp.retornoErro) {
            alertStart(resp.mensagem || "Erro ao exportar os pedidos.", "error")
            return
        }
        baixarCsv(resp.nomeArquivo || (detalhado ? "pedidosDetalhados.csv" : "pedidos.csv"), [
            resp.csv,
        ])
    }

    const qtdFiltrosAtivos = contarFiltrosAtivos(filtros)

    const totalPaginas = useMemo(() => Math.max(1, Math.ceil(total / size)), [total, size])
    const inicioFaixa = useMemo(
        () => (total === 0 ? 0 : (page - 1) * size + 1),
        [total, page, size]
    )
    const fimFaixa = useMemo(
        () => (total === 0 ? 0 : Math.min(total, (page - 1) * size + pedidos.length)),
        [total, page, size, pedidos.length]
    )

    const handleDataInicio = e => {
        const v = e.target.value
        if (!v) return setCampo("dataInicio", null)
        const [y, m, d] = v.split("-").map(Number)
        setCampo("dataInicio", inicioDoDia(new Date(y, m - 1, d)))
    }
    const handleDataFim = e => {
        const v = e.target.value
        if (!v) return setCampo("dataFim", null)
        const [y, m, d] = v.split("-").map(Number)
        setCampo("dataFim", fimDoDia(new Date(y, m - 1, d)))
    }
    const dataInputValue = d => {
        if (!d) return ""
        try {
            return format(d, "yyyy-MM-dd")
        } catch {
            return ""
        }
    }

    return (
        <Container
            component="main"
            maxWidth={false}
            className={classes.appContainer}
            style={{ paddingLeft: mobile ? 16 : 96, paddingRight: mobile ? 16 : 24 }}
        >
            <Cabecalho pagina={"Histórico de Pedidos"} />

            <Backdrop className={classes.backdrop} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <div className={classes.cabecalhoPagina}>
                <div>
                    <Typography className={classes.titulo}>Histórico de Pedidos</Typography>
                    <Typography className={classes.subtitulo} variant="body2">
                        Consulte todos os pedidos realizados
                    </Typography>
                </div>
                <div className={classes.acoesTopo}>
                    <Button
                        className={classes.btnExportar}
                        startIcon={
                            exportando === "simples" ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <CloudDownload />
                            )
                        }
                        onClick={() => exportar(false)}
                        disabled={!!exportando || !total}
                    >
                        Exportar
                    </Button>
                    <Button
                        className={classes.btnExportar}
                        startIcon={
                            exportando === "detalhado" ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <ListAlt />
                            )
                        }
                        onClick={() => exportar(true)}
                        disabled={!!exportando || !total}
                    >
                        Exportar detalhado
                    </Button>
                </div>
            </div>

            <Paper className={classes.card} elevation={0}>
                <Box
                    display="grid"
                    gridGap={12}
                    style={{
                        gridTemplateColumns: mobile
                            ? "1fr"
                            : "1fr 1fr 1fr 1fr",
                        marginBottom: 12,
                    }}
                >
                    <input
                        type="text"
                        placeholder="🔍  Buscar por código..."
                        value={filtros.codigoPedido}
                        onChange={e => setCampo("codigoPedido", e.target.value)}
                        style={{
                            padding: "10px 14px",
                            borderRadius: 8,
                            border: "1px solid #d1d5db",
                            fontSize: 14,
                            outline: "none",
                            backgroundColor: "white",
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Nome do cliente"
                        value={filtros.nomeCliente}
                        onChange={e => setCampo("nomeCliente", e.target.value)}
                        style={{
                            padding: "10px 14px",
                            borderRadius: 8,
                            border: "1px solid #d1d5db",
                            fontSize: 14,
                            outline: "none",
                            backgroundColor: "white",
                        }}
                    />
                    <input
                        type="date"
                        value={dataInputValue(filtros.dataInicio)}
                        onChange={handleDataInicio}
                        style={{
                            padding: "10px 14px",
                            borderRadius: 8,
                            border: "1px solid #d1d5db",
                            fontSize: 14,
                            outline: "none",
                            backgroundColor: "white",
                        }}
                    />
                    <input
                        type="date"
                        value={dataInputValue(filtros.dataFim)}
                        onChange={handleDataFim}
                        style={{
                            padding: "10px 14px",
                            borderRadius: 8,
                            border: "1px solid #d1d5db",
                            fontSize: 14,
                            outline: "none",
                            backgroundColor: "white",
                        }}
                    />
                </Box>

                <Box display="flex" style={{ gap: 8 }} mb={2}>
                    <Button
                        className={classes.btnPrimario}
                        onClick={handleBuscar}
                        disabled={loading}
                        startIcon={<Search />}
                    >
                        Buscar
                    </Button>
                    <Badge
                        badgeContent={qtdFiltrosAtivos}
                        invisible={!qtdFiltrosAtivos}
                        overlap="rectangular"
                        classes={{ badge: classes.badgeFiltros }}
                    >
                        <Button
                            className={classes.btnSecundario}
                            startIcon={<FilterList />}
                            onClick={() => setFiltrosAvancadosAbertos(v => !v)}
                            style={
                                filtrosAvancadosAbertos
                                    ? { borderColor: VERDE, color: VERDE_ESCURO }
                                    : undefined
                            }
                        >
                            Filtros Avançados
                        </Button>
                    </Badge>
                </Box>

                <Collapse in={filtrosAvancadosAbertos} unmountOnExit>
                    <Box pt={1} pb={2}>
                        <Filtros
                            filtros={filtros}
                            setFiltros={setFiltros}
                            formasPagamento={formasPagamento}
                            estabelecimentos={estabelecimentosRede}
                            onLimpar={handleLimpar}
                            onAplicar={handleBuscar}
                            loading={loading}
                        />
                    </Box>
                </Collapse>

                {erro && (
                    <Box
                        mb={2}
                        style={{
                            padding: 12,
                            borderRadius: 8,
                            backgroundColor: "#fef2f2",
                            border: "1px solid #fecaca",
                        }}
                    >
                        <Typography variant="body2" style={{ color: "#991b1b" }}>
                            {erro}
                        </Typography>
                        <Box mt={1}>
                            <Button
                                size="small"
                                className={classes.btnPrimario}
                                onClick={() => buscarPedidos(filtros, page, size)}
                            >
                                Tentar novamente
                            </Button>
                        </Box>
                    </Box>
                )}

                <TableContainer className={classes.tabelaContainer}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Código</StyledTableCell>
                                <StyledTableCell>Senha</StyledTableCell>
                                <StyledTableCell>Data/Hora</StyledTableCell>
                                <StyledTableCell>Cliente</StyledTableCell>
                                <StyledTableCell>Telefone</StyledTableCell>
                                <StyledTableCell>Plataforma</StyledTableCell>
                                <StyledTableCell align="right">Valor</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Pagamento</StyledTableCell>
                                <StyledTableCell>Tipo</StyledTableCell>
                                <StyledTableCell align="center">Ações</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pedidos.length === 0 && !loading && !erro && (
                                <TableRow>
                                    <TableCell colSpan={11} align="center">
                                        <Box py={3}>
                                            <Typography variant="body2" color="textSecondary">
                                                Nenhum pedido encontrado para os filtros aplicados.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                            {pedidos.map(p => {
                                const pagamento = Array.isArray(p.payments)
                                    ? p.payments.map(x => x.name).join(", ")
                                    : p.payments?.name
                                const pillTipo = tipoPill(p.type)
                                const pillStatus = corStatusPill(p.descricaoStatus)
                                return (
                                    <TableRow key={p.id} hover>
                                        <StyledTableCell style={{ fontWeight: 600 }}>
                                            #{p.id}
                                        </StyledTableCell>
                                        <StyledTableCell style={{ fontWeight: 600 }}>
                                            {p.senha || "—"}
                                        </StyledTableCell>
                                        <StyledTableCell style={{ whiteSpace: "nowrap" }}>
                                            {formatarDataHora(p)}
                                        </StyledTableCell>
                                        <StyledTableCell>{p.customer?.name}</StyledTableCell>
                                        <StyledTableCell>{p.customer?.phone}</StyledTableCell>
                                        <StyledTableCell>{plataformaPedido(p)}</StyledTableCell>
                                        <StyledTableCell
                                            align="right"
                                            style={{ fontWeight: 600 }}
                                        >
                                            {formatarMoeda(p.totalPrice)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <span
                                                className={classes.pill}
                                                style={{
                                                    backgroundColor: pillStatus.bg,
                                                    color: pillStatus.color,
                                                }}
                                            >
                                                {p.descricaoStatus || "—"}
                                            </span>
                                        </StyledTableCell>
                                        <StyledTableCell>{pagamento}</StyledTableCell>
                                        <StyledTableCell>
                                            <span
                                                className={classes.pill}
                                                style={{
                                                    backgroundColor: pillTipo.bg,
                                                    color: pillTipo.color,
                                                }}
                                            >
                                                {pillTipo.label}
                                            </span>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Tooltip title="Ações">
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Ações do pedido ${p.id}`}
                                                    onClick={e =>
                                                        setMenuAcoes({
                                                            anchor: e.currentTarget,
                                                            pedido: {
                                                                id: p.id,
                                                                cliente: p.customer?.name,
                                                                plataforma: p.plataforma,
                                                                latitude:
                                                                    p.deliveryAddress?.coordinates?.latitude,
                                                                longitude:
                                                                    p.deliveryAddress?.coordinates?.longitude,
                                                                podeCancelar: podeCancelar(p),
                                                            },
                                                        })
                                                    }
                                                >
                                                    <MoreVert fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </StyledTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <div className={classes.paginacao}>
                    <Typography variant="body2" style={{ color: "#6b7280" }}>
                        {total === 0
                            ? "Nenhum pedido"
                            : `Mostrando ${inicioFaixa}-${fimFaixa} de ${total} pedidos`}
                    </Typography>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Button
                            className={classes.btnSecundario}
                            disabled={page <= 1 || loading}
                            onClick={() => handleTrocarPagina(page - 1)}
                        >
                            Anterior
                        </Button>
                        <Typography
                            variant="body2"
                            style={{ color: "#6b7280", minWidth: 60, textAlign: "center" }}
                        >
                            {page} / {totalPaginas}
                        </Typography>
                        <Button
                            className={classes.btnPrimario}
                            disabled={page >= totalPaginas || loading}
                            onClick={() => handleTrocarPagina(page + 1)}
                        >
                            Próximo
                        </Button>
                    </Box>
                </div>
            </Paper>

            <ResumoPedidos dados={resumo} carregando={carregandoResumo} erro={erroResumo} />

            <MenuAcoesPedido
                menu={menuAcoes}
                onFecharMenu={() => setMenuAcoes(null)}
                autorizacao={autorizacao}
                fiscalAtivo={fiscalAtivo}
                onAlterado={() => buscarPedidos(filtros, page, size)}
                alertStart={alertStart}
            />
        </Container>
    )
}
